import {
  Alert,
  Button,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Tab,
  TextField,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip as MuiTooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Grid from "@mui/material/Grid2";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import React, { useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  useGetContentPerformanceAuthorsQuery,
  useGetContentPerformanceBooksQuery,
  useGetContentPerformanceCultureCategoriesQuery,
  useGetContentPerformanceCulturesQuery,
  useGetContentPerformanceQuotesQuery,
  useGetContentPerformanceSummaryQuery,
  useGetContentPerformanceTagsQuery,
} from "../../api/apiSlice";

type PeriodPreset = "TODAY" | "LAST_7" | "LAST_30" | "LAST_90" | "CUSTOM";

type ContentTab = "quotes" | "authors" | "books" | "cultureCategories" | "cultures" | "tags";

type SortOrder = "ASC" | "DESC";

const tabSortDefaults: Record<ContentTab, { sortBy: string; sortOrder: SortOrder }> = {
  quotes: { sortBy: "likes", sortOrder: "DESC" },
  authors: { sortBy: "totalLikes", sortOrder: "DESC" },
  books: { sortBy: "totalLikes", sortOrder: "DESC" },
  cultureCategories: { sortBy: "totalSubscribers", sortOrder: "DESC" },
  cultures: { sortBy: "subscribers", sortOrder: "DESC" },
  tags: { sortBy: "subscribers", sortOrder: "DESC" },
};

const chartSeriesConfig = {
  views: { label: "Views", color: "#2563eb" },
  likes: { label: "Likes", color: "#16a34a" },
  shares: { label: "Shares", color: "#ea580c" },
  cultureSubscribes: { label: "Culture Subscribes", color: "#7c3aed" },
  tagSubscribes: { label: "Tag Subscribes", color: "#db2777" },
} as const;

const MAX_EXPANDED_CULTURES_RENDER = 50;

type ChartSeriesKey = keyof typeof chartSeriesConfig;

const formatPct = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "-";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value}%`;
};

const getPresetDateRange = (preset: PeriodPreset): { startDate: string; endDate: string } => {
  const today = dayjs();
  const endDate = today.format("YYYY-MM-DD");

  if (preset === "TODAY") {
    return { startDate: endDate, endDate };
  }
  if (preset === "LAST_30") {
    return { startDate: today.subtract(30, "day").format("YYYY-MM-DD"), endDate };
  }
  if (preset === "LAST_90") {
    return { startDate: today.subtract(90, "day").format("YYYY-MM-DD"), endDate };
  }

  if (preset === "LAST_7") {
    return { startDate: today.subtract(7, "day").format("YYYY-MM-DD"), endDate };
  }

  return { startDate: today.subtract(7, "day").format("YYYY-MM-DD"), endDate };
};

const TAB_PANEL_HEIGHT = { xs: "clamp(340px, 58vh, 500px)", md: 560 };

const SummaryCard = ({ title, value, change, subtitle, info }: { title: string; value: string | number; change: number | null; subtitle: string; info?: string }) => (
  <Card sx={{ border: "1px solid", borderColor: "divider" }}>
    <CardContent>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        {info ? (
          <MuiTooltip title={info} arrow>
            <Box component="span" tabIndex={0} aria-label={`More information about ${title}`} sx={{ display: "inline-flex" }}>
              <InfoOutlinedIcon sx={{ fontSize: 15, color: "text.secondary" }} />
            </Box>
          </MuiTooltip>
        ) : null}
      </Box>
      <Typography variant="h4" fontWeight={700} mt={1}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
        {subtitle}
      </Typography>
      <Typography variant="caption" color={change !== null && change < 0 ? "error.main" : "success.main"} display="block" mt={1}>
        vs previous period: {formatPct(change)}
      </Typography>
    </CardContent>
  </Card>
);

const MetricHeader = ({ title }: { title: string }) => (
  <Box display="grid" gridTemplateColumns="2fr repeat(4, 1fr)" gap={1} py={1.25} px={1.5} sx={{ backgroundColor: "background.paper", borderBottom: "1px solid", borderColor: "divider" }}>
    <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
      {title}
    </Typography>
    <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
      Views
    </Typography>
    <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
      Likes
    </Typography>
    <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
      Shares
    </Typography>
    <Box display="flex" alignItems="center" gap={0.5}>
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
        Quote Engagement
      </Typography>
      <MuiTooltip title="Measures likes and shares generated per quote view." arrow>
        <Box component="span" tabIndex={0} aria-label="More information about quote engagement" sx={{ display: "inline-flex" }}>
          <InfoOutlinedIcon sx={{ fontSize: 15, color: "text.secondary" }} />
        </Box>
      </MuiTooltip>
    </Box>
  </Box>
);

const MetricRow = ({
  primary,
  secondary,
  views,
  likes,
  shares,
  engagement,
}: {
  primary: string;
  secondary?: string | null;
  views: number;
  likes: number;
  shares: number;
  engagement: number;
}) => (
  <Box display="grid" gridTemplateColumns="2fr repeat(4, 1fr)" gap={1} py={1.25} px={1.5} borderBottom="1px solid" borderColor="divider">
    <Box>
      <Typography variant="body2" fontWeight={600}>
        {primary}
      </Typography>
      {secondary ? (
        <Typography variant="caption" color="text.secondary">
          {secondary}
        </Typography>
      ) : null}
    </Box>
    <Typography variant="body2">{views.toLocaleString()}</Typography>
    <Typography variant="body2">{likes.toLocaleString()}</Typography>
    <Typography variant="body2">{shares.toLocaleString()}</Typography>
    <Typography variant="body2">{engagement.toFixed(1)}%</Typography>
  </Box>
);

const TAB_PANEL_MIN_HEIGHT = 420;

const TabLoadingState = () => (
  <Box height="100%" display="flex" alignItems="center" justifyContent="center">
    <CircularProgress />
  </Box>
);

export const ContentPerformance: React.FC = () => {
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>("LAST_30");
  const [activeTab, setActiveTab] = useState<ContentTab>("quotes");
  const [customStart, setCustomStart] = useState<Dayjs | null>(dayjs().subtract(7, "day"));
  const [customEnd, setCustomEnd] = useState<Dayjs | null>(dayjs());
  const [searchByTab, setSearchByTab] = useState<Record<ContentTab, string>>({
    quotes: "",
    authors: "",
    books: "",
    cultureCategories: "",
    cultures: "",
    tags: "",
  });
  const [pageByTab, setPageByTab] = useState<Record<ContentTab, number>>({
    quotes: 1,
    authors: 1,
    books: 1,
    cultureCategories: 1,
    cultures: 1,
    tags: 1,
  });
  const [sortByTab, setSortByTab] = useState<Record<ContentTab, { sortBy: string; sortOrder: SortOrder }>>(tabSortDefaults);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<Record<string, boolean>>({});
  const [chartSeries, setChartSeries] = useState<ChartSeriesKey[]>([
    "views",
    "likes",
    "shares",
    "cultureSubscribes",
    "tagSubscribes",
  ]);

  const dateParams = useMemo(() => {
    if (periodPreset === "CUSTOM") {
      return {
        startDate: (customStart ?? dayjs().subtract(7, "day")).format("YYYY-MM-DD"),
        endDate: (customEnd ?? dayjs()).format("YYYY-MM-DD"),
      };
    }
    return getPresetDateRange(periodPreset);
  }, [periodPreset, customStart, customEnd]);

  const baseParams = useMemo(
    () => ({
      ...dateParams,
      limit: 10,
    }),
    [dateParams],
  );

  const currentSearch = searchByTab[activeTab]?.trim();
  const currentPage = pageByTab[activeTab] ?? 1;
  const currentSort = sortByTab[activeTab] ?? tabSortDefaults[activeTab];

  const activeTabParams = useMemo(
    () => ({
      ...baseParams,
      page: currentPage,
      sortBy: currentSort.sortBy,
      sortOrder: currentSort.sortOrder,
      ...(currentSearch ? { search: currentSearch } : {}),
      ...(activeTab === "cultureCategories" ? { expand: "cultures" } : {}),
    }),
    [activeTab, baseParams, currentPage, currentSort.sortBy, currentSort.sortOrder, currentSearch],
  );

  const summaryQuery = useGetContentPerformanceSummaryQuery(baseParams);

  const quotesQuery = useGetContentPerformanceQuotesQuery(activeTabParams, { skip: activeTab !== "quotes" });
  const authorsQuery = useGetContentPerformanceAuthorsQuery(activeTabParams, { skip: activeTab !== "authors" });
  const booksQuery = useGetContentPerformanceBooksQuery(activeTabParams, { skip: activeTab !== "books" });
  const categoriesQuery = useGetContentPerformanceCultureCategoriesQuery(activeTabParams, { skip: activeTab !== "cultureCategories" });
  const culturesQuery = useGetContentPerformanceCulturesQuery(activeTabParams, { skip: activeTab !== "cultures" });
  const tagsQuery = useGetContentPerformanceTagsQuery(activeTabParams, { skip: activeTab !== "tags" });

  const isLoadingSummary = summaryQuery.isLoading || summaryQuery.isFetching;
  const summaryError = summaryQuery.error ? "Failed to load content performance summary." : null;

  const chartData = useMemo(
    () =>
      (summaryQuery.data?.data.daily ?? []).map((d) => ({
        ...d,
        shortDate: dayjs(d.date).format("MMM D"),
      })),
    [summaryQuery.data?.data.daily],
  );

  const topQuoteByEngagement = useMemo(() => {
    const rows = quotesQuery.data?.data ?? [];
    if (!rows.length) return null;
    return [...rows].sort((a, b) => (b.engagementRate ?? 0) - (a.engagementRate ?? 0))[0];
  }, [quotesQuery.data?.data]);

  const activeTabQuery =
    activeTab === "quotes"
      ? quotesQuery
      : activeTab === "authors"
        ? authorsQuery
        : activeTab === "books"
          ? booksQuery
          : activeTab === "cultureCategories"
            ? categoriesQuery
            : activeTab === "cultures"
              ? culturesQuery
              : tagsQuery;

  const activeMeta = activeTabQuery.data?.meta;

  const onTabChange = (_e: React.SyntheticEvent, value: ContentTab) => {
    setActiveTab(value);
  };

  const updateSearch = (value: string) => {
    setSearchByTab((prev) => ({ ...prev, [activeTab]: value }));
    setPageByTab((prev) => ({ ...prev, [activeTab]: 1 }));
  };

  const updateSort = (sortBy: string, sortOrder: SortOrder) => {
    setSortByTab((prev) => ({ ...prev, [activeTab]: { sortBy, sortOrder } }));
    setPageByTab((prev) => ({ ...prev, [activeTab]: 1 }));
  };

  const updatePage = (page: number) => {
    setPageByTab((prev) => ({ ...prev, [activeTab]: page }));
  };

  const toggleCategoryExpand = (id: string) => {
    setExpandedCategoryIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const sortOptionsByTab: Record<ContentTab, Array<{ label: string; value: string }>> = {
    quotes: [
      { label: "Highest Engagement", value: "engagementRate" },
      { label: "Most Liked", value: "likes" },
      { label: "Most Viewed", value: "views" },
      { label: "Most Shared", value: "shares" },
      { label: "Most Hidden", value: "hides" },
    ],
    cultures: [
      { label: "Most Subscribers", value: "subscribers" },
      { label: "Most Quotes", value: "totalQuotes" },
      { label: "Fastest Growing", value: "netGrowth" },
    ],
    tags: [
      { label: "Most Subscribers", value: "subscribers" },
      { label: "Most Quotes", value: "totalQuotes" },
      { label: "Fastest Growing", value: "netGrowth" },
    ],
    cultureCategories: [
      { label: "Most Subscribers", value: "totalSubscribers" },
      { label: "Most Cultures", value: "totalCultures" },
      { label: "Most Quotes", value: "totalQuotes" },
      { label: "Highest Avg Subs/Culture", value: "avgSubsPerCulture" },
    ],
    authors: [
      { label: "Most Liked", value: "totalLikes" },
      { label: "Most Viewed", value: "totalViews" },
      { label: "Most Shared", value: "totalShares" },
      { label: "Highest Avg Engagement", value: "avgEngRate" },
    ],
    books: [
      { label: "Most Liked", value: "totalLikes" },
      { label: "Most Viewed", value: "totalViews" },
      { label: "Most Shared", value: "totalShares" },
      { label: "Highest Avg Engagement", value: "avgEngRate" },
    ],
  };

  const searchPlaceholderByTab: Record<ContentTab, string> = {
    quotes: "Search quote, author, culture...",
    cultures: "Search culture...",
    tags: "Search tag...",
    cultureCategories: "Search category...",
    authors: "Search author...",
    books: "Search book...",
  };

  const renderTabContent = () => {
    if (activeTab === "quotes") {
      if (quotesQuery.isLoading || quotesQuery.isFetching) return <TabLoadingState />;
      if (quotesQuery.error) return <Alert severity="error">Failed to load quotes performance.</Alert>;
      return (
        <>
          {topQuoteByEngagement ? (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Top quote: {topQuoteByEngagement.engagementRate?.toFixed(1) ?? "0.0"}% quote engagement, "{topQuoteByEngagement.quote}" by{" "}
              {topQuoteByEngagement.author ?? "Unknown"}.
            </Alert>
          ) : null}
          <MetricHeader title="Quote" />
          {(quotesQuery.data?.data ?? []).map((row) => (
            <Box key={row.id}>
              <MetricRow
                primary={row.quote}
                secondary={[row.author, row.culture].filter(Boolean).join(" • ")}
                views={row.views}
                likes={row.likes}
                shares={row.shares}
                engagement={row.engagementRate ?? 0}
              />
            </Box>
          ))}
        </>
      );
    }

    if (activeTab === "authors") {
      if (authorsQuery.isLoading || authorsQuery.isFetching) return <TabLoadingState />;
      if (authorsQuery.error) return <Alert severity="error">Failed to load authors performance.</Alert>;
      return (
        <>
          <MetricHeader title="Author" />
          {(authorsQuery.data?.data ?? []).map((row) => (
            <MetricRow
              key={row.id}
              primary={row.name}
              secondary={`Quotes: ${row.totalQuotes}`}
              views={row.totalViews}
              likes={row.totalLikes}
              shares={row.totalShares}
              engagement={row.avgEngRate ?? 0}
            />
          ))}
        </>
      );
    }

    if (activeTab === "books") {
      if (booksQuery.isLoading || booksQuery.isFetching) return <TabLoadingState />;
      if (booksQuery.error) return <Alert severity="error">Failed to load books performance.</Alert>;
      return (
        <>
          <MetricHeader title="Book" />
          {(booksQuery.data?.data ?? []).map((row) => (
            <MetricRow
              key={row.id}
              primary={row.name}
              secondary={row.author ? `${row.author} • Quotes: ${row.totalQuotes}` : `Quotes: ${row.totalQuotes}`}
              views={row.totalViews}
              likes={row.totalLikes}
              shares={row.totalShares}
              engagement={row.avgEngRate ?? 0}
            />
          ))}
        </>
      );
    }

    if (activeTab === "cultureCategories") {
      if (categoriesQuery.isLoading || categoriesQuery.isFetching) return <TabLoadingState />;
      if (categoriesQuery.error) return <Alert severity="error">Failed to load culture category performance.</Alert>;
      return (
        <Box>
          {(categoriesQuery.data?.data ?? []).map((row) => (
            <Card key={row.id} sx={{ mb: 1.5, border: "1px solid", borderColor: "divider" }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700}>
                  {row.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Cultures: {row.totalCultures} • Subscribers: {row.totalSubscribers} • Active quotes in selected period: {row.totalQuotes}
                </Typography>
                <Button size="small" sx={{ mt: 1 }} onClick={() => toggleCategoryExpand(row.id)}>
                  {expandedCategoryIds[row.id] ? "Hide cultures" : "Show cultures"}
                </Button>
                {expandedCategoryIds[row.id] && (row.cultures?.length ?? 0) > 0 ? (
                  <Box
                    mt={1.5}
                    sx={{
                      maxHeight: 260,
                      overflowY: "auto",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    <Stack spacing={0.75}>
                      {row.cultures?.slice(0, MAX_EXPANDED_CULTURES_RENDER).map((c) => (
                        <Box
                          key={c.id}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          gap={1}
                          sx={{ borderBottom: "1px dashed", borderColor: "divider", pb: 0.5 }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {c.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {c.subscribers} subscribers, {c.totalQuotes} quotes
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                    {(row.cultures?.length ?? 0) > MAX_EXPANDED_CULTURES_RENDER ? (
                      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                        Showing first {MAX_EXPANDED_CULTURES_RENDER} of {row.cultures?.length} cultures.
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </Box>
      );
    }

    if (activeTab === "cultures") {
      if (culturesQuery.isLoading || culturesQuery.isFetching) return <TabLoadingState />;
      if (culturesQuery.error) return <Alert severity="error">Failed to load cultures performance.</Alert>;
      return (
        <Box>
          {(culturesQuery.data?.data ?? []).map((row) => (
            <Card key={row.id} sx={{ mb: 1.5, border: "1px solid", borderColor: "divider" }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700}>
                  {row.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Category: {row.category ?? "-"} • Subscribers: {row.subscribers} • Net growth: {row.netGrowth ?? 0} • Active quotes in selected period: {row.totalQuotes}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      );
    }

    if (tagsQuery.isLoading || tagsQuery.isFetching) return <TabLoadingState />;
    if (tagsQuery.error) return <Alert severity="error">Failed to load tags performance.</Alert>;
    return (
      <Box>
        {(tagsQuery.data?.data ?? []).map((row) => (
          <Card key={row.id} sx={{ mb: 1.5, border: "1px solid", borderColor: "divider" }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700}>
                {row.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                Type: {row.type ?? "-"} • Subscribers: {row.subscribers} • Net growth: {row.netGrowth ?? 0} • Active quotes in selected period: {row.totalQuotes}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Box display="flex" justifyContent="flex-end" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="period-preset-label">Period</InputLabel>
              <Select
                labelId="period-preset-label"
                label="Period"
                value={periodPreset}
                onChange={(e) => setPeriodPreset(e.target.value as PeriodPreset)}
              >
                <MenuItem value="TODAY">Today</MenuItem>
                <MenuItem value="LAST_7">Last 7 days</MenuItem>
                <MenuItem value="LAST_30">Last 30 days</MenuItem>
                <MenuItem value="LAST_90">Last 90 days</MenuItem>
                <MenuItem value="CUSTOM">Custom range</MenuItem>
              </Select>
            </FormControl>
            {periodPreset === "CUSTOM" ? (
              <>
                <DatePicker
                  label="Start Date"
                  value={customStart}
                  onChange={(v) => setCustomStart(v)}
                  maxDate={customEnd ?? dayjs()}
                  slotProps={{ textField: { size: "small" } }}
                />
                <DatePicker
                  label="End Date"
                  value={customEnd}
                  onChange={(v) => setCustomEnd(v)}
                  minDate={customStart ?? undefined}
                  maxDate={dayjs()}
                  slotProps={{ textField: { size: "small" } }}
                />
              </>
            ) : null}
          </Box>
        </Box>

        {summaryError ? <Alert severity="error">{summaryError}</Alert> : null}

        {isLoadingSummary ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} mb={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <SummaryCard
                title="Views"
                value={summaryQuery.data?.data.summary.views.value ?? 0}
                change={summaryQuery.data?.data.summary.views.change ?? null}
                subtitle="Quote views in selected period"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <SummaryCard
                title="Likes"
                value={summaryQuery.data?.data.summary.likes.value ?? 0}
                change={summaryQuery.data?.data.summary.likes.change ?? null}
                subtitle="Quote likes in selected period"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <SummaryCard
                title="Shares"
                value={summaryQuery.data?.data.summary.shares.value ?? 0}
                change={summaryQuery.data?.data.summary.shares.change ?? null}
                subtitle="Quote shares in selected period"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <SummaryCard
                title="Quote Engagement Rate"
                value={`${summaryQuery.data?.data.summary.engagementRate.value ?? 0}%`}
                change={summaryQuery.data?.data.summary.engagementRate.change ?? null}
                subtitle="(Likes + shares) / quote views"
                info="How much engagement did we get from quote views?"
              />
            </Grid>
          </Grid>
        )}

        <Card sx={{ border: "1px solid", borderColor: "divider", mb: 2 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <Typography variant="subtitle1">Engagement Trend</Typography>
              <ToggleButtonGroup
                size="small"
                value={chartSeries}
                onChange={(_e, value) => setChartSeries((current) => (value.length ? value : current))}
              >
                {(Object.keys(chartSeriesConfig) as ChartSeriesKey[]).map((key) => (
                  <ToggleButton key={key} value={key}>
                    {chartSeriesConfig[key].label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <Box height={280}>
              {isLoadingSummary ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="shortDate" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    {chartSeries.map((key) => (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        name={chartSeriesConfig[key].label}
                        stroke={chartSeriesConfig[key].color}
                        strokeWidth={2}
                        dot={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ border: "1px solid", borderColor: "divider" }}>
          <Box px={2} pt={1.5}>
            <Tabs
              value={activeTab}
              onChange={onTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab value="quotes" label="Quotes" />
              <Tab value="cultures" label="Cultures" />
              <Tab value="tags" label="Tags" />
              <Tab value="cultureCategories" label="Categories" />
              <Tab value="authors" label="Authors" />
              <Tab value="books" label="Books" />
            </Tabs>
          </Box>
          <Divider />
          <Box p={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} mb={2}>
              <TextField
                size="small"
                fullWidth
                label="Search"
                placeholder={searchPlaceholderByTab[activeTab]}
                value={searchByTab[activeTab]}
                onChange={(e) => updateSearch(e.target.value)}
              />
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel id="sort-by-label">Sort By</InputLabel>
                <Select
                  labelId="sort-by-label"
                  label="Sort By"
                  value={currentSort.sortBy}
                  onChange={(e) => updateSort(String(e.target.value), currentSort.sortOrder)}
                >
                  {sortOptionsByTab[activeTab].map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id="sort-order-label">Order</InputLabel>
                <Select
                  labelId="sort-order-label"
                  label="Order"
                  value={currentSort.sortOrder}
                  onChange={(e) => updateSort(currentSort.sortBy, e.target.value as SortOrder)}
                >
                  <MenuItem value="DESC">DESC</MenuItem>
                  <MenuItem value="ASC">ASC</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Box
              sx={{
                minHeight: TAB_PANEL_MIN_HEIGHT,
                height: TAB_PANEL_HEIGHT,
                overflowY: "auto",
                pr: 0.5,
              }}
            >
              {renderTabContent()}
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} flexWrap="wrap" gap={1}>
              <Typography variant="caption" color="text.secondary">
                Page {activeMeta?.page ?? 1} of {activeMeta?.totalPages ?? 1} • Total items: {activeMeta?.totalItems ?? 0}
              </Typography>
              <Pagination
                size="small"
                page={activeMeta?.page ?? currentPage}
                count={Math.max(activeMeta?.totalPages ?? 1, 1)}
                onChange={(_e, p) => updatePage(p)}
              />
            </Box>
          </Box>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};
